import axios from "axios";
import store from "../store";
import { logout } from "../store/auth.slice";
import { AFID } from "../utils/localstore";

export const envConfig = process.env;
export const globalConfig = {
    domainName: 'data2go',
    dataSize: 'GB',
    defaultCurrency: '$',
    libraries: ['places'],
    allowPhoneNumberLength: 16,
    paymentDefaultCountry: 'US',
    paymentDefaultLabel: 'eSIM Purchase',
    pageCount: 20,
    domainUrl: "https://data2go.mobi",
    supportEmail: 'support@data2go.mobi',
    contactEmail: 'contact@data2go.mobi',
    salesEmail: 'sales@data2go.mobi',
    phoneNumber: "+44 1223 926858",
    allowNameLength: 20,
    toastDisplayTime: 10000,
    deviceInfo: "website",
    isEsimCompatible: false,
    catalogDataAmountperGB: 1000,
    notificationCount: 20,
    restrictProviders: [2, 3],
    iosVersionForActivateEsim: "17.4",
    appStoreLink:"https://apps.apple.com/us/app/data2go/id6733241999",
    playStoreLink:"https://play.google.com/store/apps/details?id=com.mobi.data2go"
}

export const client = axios.create({
    baseURL: '/v1/',
});

export const clientV2 = axios.create({
    baseURL: '/v2/',
});


// Setting the session ID for each api request if session ID available in redux Store
client.interceptors.request.use(
    config => {
        const state = store.getState();
        const sessionId = state?.auth?.userData?.sessionId;
        const afid = state?.auth?.afid;
        if (!!sessionId) config.headers['Sessionid'] = sessionId;
        if(!!afid) config.headers[AFID] = afid;
        return config;
    },
    error => Promise.reject(error)
);

clientV2.interceptors.request.use(
    config => {
        const state = store.getState();
        const sessionId = state?.auth?.userData?.sessionId;
        const afid = state?.auth?.afid;
        if (sessionId) config.headers['Sessionid'] = sessionId;
        if (afid) config.headers[AFID] = afid;
        return config;
    },
    error => Promise.reject(error)
);


const handleKycRedirect = error => {
    if (error?.response?.data?.errorMsg) {
        const errorMsg = error?.response?.data?.errorMsg;
        const isKyc03 = errorMsg === "KYC-03";
        if (isKyc03 && typeof window !== "undefined") {
            window.location.href = "/kyc-details";
        }
    }
};

// Logging out the user if the response is unauthenticated on each api response 
client.interceptors.response.use(
    res => res,
    error => {
        if (error.response && error.response.status === 401) {
            store.dispatch(logout());
        }
        handleKycRedirect(error);
        return Promise.reject(error)
    }
)

clientV2.interceptors.response.use(
    res => res,
    error => {
        if (error.response && error.response.status === 401) {
            store.dispatch(logout());
        }
        handleKycRedirect(error);
        return Promise.reject(error);
    }
);

const apiWithSessionId = data => client(data)
    .then(res => {
         const redirectLocation = res?.headers['bht-location'];
        if (redirectLocation) {
            window.location.href = redirectLocation
        }
        return ({ ...res.data, sessionId: res.headers.sessionid })
    })
    .catch((err) => { 
        const redirectLocation = err?.response?.headers['bht-location'];
        if (redirectLocation) {
            window.location.href = redirectLocation
        }
        throw new Error(err?.response?.data?.errorMsg ?? "GNRMSG-07")
     });

export const api = data => client(data)
    .then((res) => res.data)
    .catch((err) => { throw new Error(err?.response?.data?.errorMsg ?? "GNRMSG-07"); });

export const apiV2 = data => clientV2(data)
    .then((res) => res.data )
    .catch((err) => { throw new Error(err?.response?.data?.errorMsg ?? "GNRMSG-07"); });


export const getConfig = () => api({
    method: 'get',
    url: 'config'
});

export const getBundleslist = url => api({
    method: 'get',
    url: `catalogs?${url}`
});

export const getBundleDetail = name => api({
    method: 'get',
    url: `catalogs/${name}`
});

export const getCountries = () => client({ method: 'get', url: `countries` })
    .then((res) => ({ data: res.data, ipcountry: res.headers['x-cf-ipcountry'] }))
    .catch((err) => { throw new Error(err?.response?.data?.errorMsg ?? "GNRMSG-07"); });

export const handleSignup = data => apiWithSessionId({
    method: 'post',
    url: "signup",
    data
});

export const handleSignin = data => apiWithSessionId({ method: 'post', url: 'login', data })

export const googleLoginAPI = code => apiWithSessionId({ method: 'get', url: `googlelogin?code=${code}` })

export const appleLoginAPI = data => apiWithSessionId({ method: 'post', url: 'appleloginweb', data })

//affiliate contact
export const affiliateSignup = data => api({ method: 'post', url: 'contact', data });

export const verifyEmail = (url, data) => api({
    method: 'post',
    url,
    data
});

export const resendOTP = data => api({
    method: 'post',
    url: 'reverifyemail',
    data
});

export const googleLogin = (code) => api({
    method: 'get',
    url: `googlelogin?code=${code}`
});

export const updateProfile = (data) => api({
    method: 'put',
    url: `profile`,
    data
});

export const getMyPlan = () => api({
    method: 'get',
    url: `countries`
});

export const paymentMethodAPI = data => api({
    method: 'post',
    url: data.paymentGateway === "Paypal" ? "paypalpayment" : `stripepayment`,
    data
});

export const validateCouponAPI = data => api({
    method: 'post',
    url: `validatepromocode`,
    data
});


export const getAllcountry = () => api({
    method: 'get',
    url: `countryinfo?countries=&info=`
})

export const changeUserPassword = (data) => api({
    method: 'put',
    url: `changepassword`,
    data,
})

export const forgotPassword = (email) => api({
    method: 'get',
    url: `forgotpassword?email=${encodeURIComponent(email)}`
})

export const resetPassword = ({ passwordResetUUID, password }) => api({
    method: 'put',
    url: `resetpassword`,
    data: {
        passwordResetUUID,
        password
    }
})

export const myPlan = () => api({
    method: 'get',
    url: `esims`
})

export const fetchEsimIccids = () => apiV2({
    method: 'get',
    url: `esims`,
});

export const fetchEsimDetails = (iccid) => api({
    method: 'get',
    url: `esims/${iccid}` 
});

export const getOrderDetails = (data) => api({
    method: 'post',
    url: `orders`,
    data
})
export const getOrderDetailsByIdApi = (orderId) => api({
    method: 'get',
    url: `orders/${orderId}/public`,
})

export const editTag = (data) => api({
    method: 'put',
    url: `edittag`,
    data,
})


export const getCountriesList = (url) => api({
    method: 'get',
    url: url
});

export const contactus = (data) => api({
    method: "post",
    url: 'contact',
    data
})

export const getNotifications = (url) => api({
    method: "get",
    url: `notifications?${url}`
});

export const getlanguageAPI = (ln,screen) => api({ method: 'get', url: `assets?language=${ln}&screen=${screen}` })

export const redeemVoucherAPI = data => api({ method: 'post', url: 'redeemvoucher', data });

export const getTopUpPrepaidBundle = bundleName => api({ method: 'get', url: `topupprepaidbundle?bundle=${bundleName}` });

export const discountPayment = data => api({ method: 'post', url: 'freeorder', data });

export const deleteAccountAPI = () => api({
    method: "get",
    url: "deleteuser"
});

export const getCheckoutSessionAPI = data => api({method: "post", url: "stripe/session", data});
export const confirmPaymentAPI = data => api({method: "get", url: "stripe/session/checkout" + data});

export const getPaypalCheckoutSessionAPI = data => api({method: "post", url: "paypal/session", data});
export const confirmPaypalPaymentAPI = (orderID, randomId) => api({method: "get", url: `paypal/session/checkout?orderid=${orderID}&random_id=${randomId}`});

export const notifyEmail = data => api({
    method: 'post',
    url: 'notifyemail',
    data
});

export const getKycStatusApi = () => api({
    method: "get",
    url: "kyc/status",
});

export const initializeKycVerificationApi = () => api({
    method: "post",
    url: "kyc/session",
});
